import { useMemo, useState, type FormEvent } from "react";

import { Button, Icon, type IconName, Modal, Select } from "@/components/common";
import { useClients } from "@/hooks/useClients";
import { useUpdateMember } from "@/hooks/useInvitations";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useRoles } from "@/hooks/useRoles";
import { extractErrorMessage } from "@/services/axiosConfig";
import type { GrantScope, Member } from "@/types/invitation";

const SCOPE_OPTIONS: { value: GrantScope; icon: IconName; title: string; desc: string }[] = [
  {
    value: "workspace",
    icon: "building",
    title: "Workspace inteiro",
    desc: "Acesso a todas as orgs e empresas atuais e futuras.",
  },
  { value: "organization", icon: "layers", title: "Organizações", desc: "Só organizações específicas." },
  { value: "client", icon: "briefcase", title: "Empresas", desc: "Só CNPJs específicos." },
];

type Props = {
  workspaceId: number;
  member: Member;
  onClose: () => void;
};

export function EditMemberModal({ workspaceId, member, onClose }: Props) {
  const { data: roles = [] } = useRoles(workspaceId);
  const { data: orgs = [] } = useOrganizations(workspaceId);
  const { data: clients = [] } = useClients(workspaceId);
  const update = useUpdateMember(workspaceId);

  const initialRole = roles.find((r) => r.slug === member.role_slug);
  const [roleId, setRoleId] = useState<number | null>(initialRole?.id ?? null);
  const [scope, setScope] = useState<GrantScope>(member.scope as GrantScope);
  const [orgIds, setOrgIds] = useState<number[]>(member.organization_ids ?? []);
  const [clientIds, setClientIds] = useState<number[]>(member.client_ids ?? []);
  const [error, setError] = useState<string | null>(null);

  // roles só carrega async — se o select ainda não tem valor, casa pelo slug.
  const effectiveRoleId = roleId ?? roles.find((r) => r.slug === member.role_slug)?.id ?? null;

  const canSubmit = useMemo(() => {
    if (effectiveRoleId == null) return false;
    if (scope === "organization" && orgIds.length === 0) return false;
    if (scope === "client" && clientIds.length === 0) return false;
    return true;
  }, [effectiveRoleId, scope, orgIds, clientIds]);

  function toggle(list: number[], id: number): number[] {
    return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await update.mutateAsync({
        grantId: member.id,
        payload: {
          role_id: effectiveRoleId ?? undefined,
          scope,
          organization_ids: scope === "organization" ? orgIds : [],
          client_ids: scope === "client" ? clientIds : [],
        },
      });
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  return (
    <Modal
      onClose={onClose}
      title="Editar acesso"
      subtitle={member.user_name ?? member.user_email_masked}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="edit-member-form"
            disabled={update.isPending || !canSubmit}
          >
            {update.isPending ? "Salvando…" : "Salvar"}
          </Button>
        </>
      }
    >
      <form
        id="edit-member-form"
        onSubmit={onSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        {error && <div className="form-error">{error}</div>}

        <label className="form-field">
          <span>Papel (role)</span>
          <Select
            value={effectiveRoleId == null ? null : String(effectiveRoleId)}
            onChange={(v) => setRoleId(v == null ? null : Number(v))}
            options={roles.map((r) => ({ value: String(r.id), label: r.name }))}
            placeholder="Selecione…"
          />
        </label>

        <div className="form-field">
          <span>Escopo de acesso</span>
          <div className="scope-cards">
            {SCOPE_OPTIONS.map((s) => (
              <button
                type="button"
                key={s.value}
                className={`scope-card ${scope === s.value ? "active" : ""}`}
                onClick={() => setScope(s.value)}
              >
                <Icon name={s.icon} size={18} />
                <span className="scope-card-title">{s.title}</span>
                <span className="scope-card-desc">{s.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {scope === "organization" && (
          <fieldset className="invite-checklist">
            <legend>Organizações cobertas</legend>
            {orgs.map((o) => (
              <label key={o.id}>
                <input
                  type="checkbox"
                  checked={orgIds.includes(o.id)}
                  onChange={() => setOrgIds((l) => toggle(l, o.id))}
                />
                {o.nickname ?? o.legal_name}
              </label>
            ))}
            {orgs.length === 0 && <p className="td-small">Nenhuma organização cadastrada.</p>}
          </fieldset>
        )}
        {scope === "client" && (
          <fieldset className="invite-checklist">
            <legend>Empresas cobertas</legend>
            {clients.map((c) => (
              <label key={c.id}>
                <input
                  type="checkbox"
                  checked={clientIds.includes(c.id)}
                  onChange={() => setClientIds((l) => toggle(l, c.id))}
                />
                {c.legal_name}
              </label>
            ))}
            {clients.length === 0 && <p className="td-small">Nenhuma empresa cadastrada.</p>}
          </fieldset>
        )}
      </form>
    </Modal>
  );
}
